import type { SplitSort } from '../../model/types';
import { SplitSortBy, SplitSortDirection } from '../../model/types';

interface ResolveSplitOrderParams<T> {
  options: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  sort: SplitSort | undefined;
  isChronologicalSplit: boolean;
  valueByOption?: Map<string, number>;
}

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

// Lenient: some Date values (`2026-W38`) defeat `Date`, so numeric collation is used.
const compareChronologically = (a: string, b: string): number => {
  const aTime = new Date(a).getTime();
  const bTime = new Date(b).getTime();

  if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
    return collator.compare(a, b);
  }

  return aTime - bTime;
};

export const resolveSplitOrder = <T,>({
  options,
  getKey,
  getLabel,
  sort,
  isChronologicalSplit,
  valueByOption,
}: ResolveSplitOrderParams<T>): T[] => {
  if (!sort) {
    return options;
  }

  const sign = sort.direction === SplitSortDirection.asc ? 1 : -1;

  const compare = (a: T, b: T): number => {
    if (sort.by === SplitSortBy.value) {
      const aValue = valueByOption?.get(getKey(a));
      const bValue = valueByOption?.get(getKey(b));

      // Absent sinks both directions (no `sign`); compared to `undefined`, not falsiness — 0 is real.
      if (aValue === undefined || bValue === undefined) {
        if (aValue === bValue) {
          return 0;
        }

        return aValue === undefined ? 1 : -1;
      }

      return (aValue - bValue) * sign;
    }

    const labelA = getLabel(a);
    const labelB = getLabel(b);

    return (
      (isChronologicalSplit
        ? compareChronologically(labelA, labelB)
        : collator.compare(labelA, labelB)) * sign
    );
  };

  return [...options].sort(compare);
};
